import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from '@/i18n/navigation';

type ButtonProps = {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'outline-inverse' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  className?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

// Hierarchy is carried by fill, not by hue. `signal` belongs to the primary
// action alone — the moment a second element wears it, the page loses the one
// thing that tells a visitor where to click.
const variants = {
  primary: 'bg-signal text-white hover:bg-signal-hover',
  secondary: 'bg-ink text-white hover:bg-ink/85',
  outline: 'bg-surface text-ink border border-line hover:border-ink',
  // The secondary action on a `deep` surface. Never filled: `signal` stays
  // the only fill above the fold.
  'outline-inverse': 'text-white border border-white/30 hover:border-white',
  ghost: 'text-ink hover:bg-ink/5',
};

const sizes = {
  sm: 'px-4 py-2 text-body-sm',
  md: 'px-6 py-3 text-body-sm',
  lg: 'px-7 py-3.5 text-body',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  className = '',
  ...props
}: ButtonProps) {
  const classes = `inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors ${variants[variant]} ${sizes[size]} ${className}`;

  if (href) {
    return (
      <Link href={href} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <button className={classes} {...props}>
      {children}
    </button>
  );
}
